import * as React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useNavigate, useLocation } from 'react-router-dom';
import { AppBar, Toolbar, Button, IconButton, Avatar, Tooltip, Box, useMediaQuery } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import { USER_TYPE_ADMIN, USER_TYPE_WAREHOUSE } from '../../types/auth/types';

import LeftMenuDrawer from 'components/AppBar/LeftMenuDrawer';
import AccountDropdownMenu from 'components/AppBar/AccountDropdownMenu';

// Create search bar context
export const SearchBarContainerContext = React.createContext<{
  container: HTMLDivElement | null;
  setContainer: (node: HTMLDivElement | null) => void;
  // eslint-disable-next-line @typescript-eslint/no-empty-function
}>({ container: null, setContainer: () => {} });

export default function TopAppBar() {
  const isMobileView = useMediaQuery('(max-width:600px)');
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { setContainer } = React.useContext(SearchBarContainerContext);

  const [leftMenuDrawerOpened, setLeftMenuDrawerOpened] = React.useState(false);
  const [accountMenuAnchorEl, setAccountMenuAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleAccountMenuIconClick = (event: React.MouseEvent<HTMLElement>) =>
    setAccountMenuAnchorEl(event.currentTarget);

  const user = useSelector((state: RootState) => state.auth.user);
  const userInitials = [user?.first_name, user?.last_name].map(name => name?.[0] ?? '').join('');

  const isHomeButtonActive = pathname === '/home';
  const isVehiclesButtonActive = pathname.startsWith('/home/vehicles');
  const isUsersButtonActive = pathname.startsWith('/home/users');
  const isEntitiesButtonActive = pathname.startsWith('/home/entities');
  const isVinDecoderButtonActive = pathname.startsWith('/home/vin-decoder');

  const activeButtonStyles = {
    'color': '#fff',
    'backgroundColor': '#000',
    'borderRadius': '30px',
    'padding': '0 15px',
    '&:hover': {
      backgroundColor: '#000',
    },
  };

  const notActiveButtonStyles = {
    'color': '#000',
    'borderRadius': '30px',
    'padding': '0 15px',
    '&:hover': {
      backgroundColor: '#f4f4f4',
    },
  };

  return (
    <AppBar position="sticky" color="transparent">
      <Toolbar disableGutters sx={{ position: 'relative', mx: 2 }}>
        {isMobileView ? (
          <>
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => setLeftMenuDrawerOpened(prev => !prev)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <LeftMenuDrawer
              opened={leftMenuDrawerOpened}
              setOpened={setLeftMenuDrawerOpened}
              adminUser={user?.user_type === USER_TYPE_ADMIN || false}
              warehouseUser={user?.user_type === USER_TYPE_WAREHOUSE || false}
              isHomeButtonActive={isHomeButtonActive}
              isVehiclesButtonActive={isVehiclesButtonActive}
              isUsersButtonActive={isUsersButtonActive}
              isEntitiesButtonActive={isEntitiesButtonActive}
              isVinDecoderButtonActive={isVinDecoderButtonActive}
            />
          </>
        ) : (
          <div
            style={{
              display: 'flex',
              width: 'calc(100% - 200px)',
              height: '30px',
              marginLeft: '150px',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', gap: '10px' }}>
              {user?.user_type !== USER_TYPE_WAREHOUSE ? (
                <Button
                  variant={isHomeButtonActive ? 'contained' : 'text'}
                  sx={isHomeButtonActive ? activeButtonStyles : notActiveButtonStyles}
                  onClick={() => navigate('/home')}
                >
                  Dashboard
                </Button>
              ) : null}
              {user?.user_type === USER_TYPE_ADMIN || user?.user_type === USER_TYPE_WAREHOUSE ? (
                <Button
                  variant={isVehiclesButtonActive ? 'contained' : 'text'}
                  sx={isVehiclesButtonActive ? activeButtonStyles : notActiveButtonStyles}
                  onClick={() => navigate('/home/vehicles')}
                >
                  Vehicles
                </Button>
              ) : null}
              {user?.user_type === USER_TYPE_ADMIN ? (
                <>
                  <Button
                    variant={isUsersButtonActive ? 'contained' : 'text'}
                    sx={isUsersButtonActive ? activeButtonStyles : notActiveButtonStyles}
                    onClick={() => navigate('/home/users')}
                  >
                    Users
                  </Button>
                  <Button
                    variant={isEntitiesButtonActive ? 'contained' : 'text'}
                    sx={isEntitiesButtonActive ? activeButtonStyles : notActiveButtonStyles}
                    onClick={() => navigate('/home/entities')}
                  >
                    Entities
                  </Button>
                  <Button
                    variant={isVinDecoderButtonActive ? 'contained' : 'text'}
                    sx={isVinDecoderButtonActive ? activeButtonStyles : notActiveButtonStyles}
                    onClick={() => navigate('/home/vin-decoder')}
                  >
                    VIN Decoder
                  </Button>
                </>
              ) : null}
            </div>
            <div
              ref={setContainer}
              style={{ flexGrow: '1', alignSelf: 'center', maxWidth: '600px', marginLeft: '50px' }}
            ></div>
          </div>
        )}
        <Box
          component="img"
          sx={{
            position: 'absolute',
            width: '110px',
            height: '100px',
            left: isMobileView ? 'calc(50% - 55px)' : '14px',
            top: 'calc(50% - 50px)',
            objectFit: 'contain',
            cursor: 'pointer',
          }}
          alt="logo"
          src="/better_cargo_logo_black.svg"
          onClick={() => navigate(user?.user_type === USER_TYPE_WAREHOUSE ? '/home/vehicles' : '/home')}
        />
        <div style={{ marginLeft: 'auto' }}>
          <Tooltip title="Account settings">
            <IconButton
              onClick={handleAccountMenuIconClick}
              size="small"
              sx={{ ml: 2 }}
              aria-controls={accountMenuAnchorEl ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={accountMenuAnchorEl ? 'true' : undefined}
              color="inherit"
            >
              {userInitials ? (
                <Avatar sx={{ width: 40, height: 40, bgcolor: '#000', color: '#fff' }}>{userInitials}</Avatar>
              ) : (
                <AccountCircle
                  sx={{
                    width: 40,
                    height: 40,
                  }}
                />
              )}
            </IconButton>
          </Tooltip>
        </div>
        <AccountDropdownMenu
          accountMenuAnchorEl={accountMenuAnchorEl}
          setAccountMenuAnchorEl={setAccountMenuAnchorEl}
        />
      </Toolbar>
    </AppBar>
  );
}
